import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, TrendingUp, DollarSign, Calendar, AlertCircle, LogOut, Shield, UserCheck, UserX, Tag, Save, X, LayoutDashboard, Package, Eye, ArrowLeft, Power, PowerOff } from 'lucide-react';
import { auth, db } from './firebase';
import { signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, collection, getDocs, addDoc, updateDoc, deleteDoc, query, where, Timestamp, setDoc } from 'firebase/firestore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ALLOWED_DOMAIN = '@soho.cl';

const CostTracker = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({ name: '', color: 'blue' });
  const [selectedService, setSelectedService] = useState(null);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    email: '',
    name: '',
    role: 'Usuario',
    active: true
  });

  const checkUserAuthorization = async (userEmail) => {
    try {
      const userDoc = await getDoc(doc(db, 'authorizedUsers', userEmail));
      return userDoc.exists() && userDoc.data().active === true;
    } catch (error) {
      console.error('Error verificando autorización:', error);
      return false;
    }
  };

  const checkIfAdmin = async (userEmail) => {
    try {
      const adminDoc = await getDoc(doc(db, 'admins', userEmail));
      return adminDoc.exists() && adminDoc.data().isAdmin === true;
    } catch (error) {
      console.error('Error verificando admin:', error);
      return false;
    }
  };

  const loadAuthorizedUsers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'authorizedUsers'));
      const users = [];
      querySnapshot.forEach((doc) => {
        users.push({ email: doc.id, ...doc.data() });
      });
      setAuthorizedUsers(users);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'categories'));
      const cats = [];
      querySnapshot.forEach((doc) => {
        cats.push({ id: doc.id, ...doc.data() });
      });
      cats.sort((a, b) => (a.order || 999) - (b.order || 999));
      setCategories(cats);
    } catch (error) {
      console.error('Error cargando categorías:', error);
    }
  };

  // NUEVA FUNCIÓN: Calcular la próxima fecha de renovación
  const calculateNextRenewalDate = (currentRenewalDate, frequency) => {
    const today = new Date();
    let renewalDate = new Date(currentRenewalDate);
    
    // Si la fecha de renovación ya pasó, calcular la siguiente
    while (renewalDate < today) {
      if (frequency === 'Mensual') {
        renewalDate.setMonth(renewalDate.getMonth() + 1);
      } else if (frequency === 'Trimestral') {
        renewalDate.setMonth(renewalDate.getMonth() + 3);
      } else if (frequency === 'Anual') {
        renewalDate.setFullYear(renewalDate.getFullYear() + 1);
      }
    }
    
    return renewalDate.toISOString().split('T')[0];
  };

  // FUNCIÓN MODIFICADA: Actualizar automáticamente las fechas de renovación
  const updateRenewalDatesIfNeeded = async (servs) => {
    const today = new Date();
    const updates = [];

    for (const service of servs) {
      const renewalDate = new Date(service.renewalDate);
      
      // Si la fecha de renovación ya pasó, actualizarla
      if (renewalDate < today) {
        const newRenewalDate = calculateNextRenewalDate(service.renewalDate, service.frequency);
        
        updates.push(
          updateDoc(doc(db, 'services', service.id), {
            renewalDate: newRenewalDate
          })
        );
        
        // Actualizar localmente
        service.renewalDate = newRenewalDate;
      }
    }

    if (updates.length > 0) {
      await Promise.all(updates);
      console.log(`Actualizadas ${updates.length} fechas de renovación`);
    }

    return servs;
  };

  // FUNCIÓN MODIFICADA: Cargar servicios y ordenarlos alfabéticamente
  const loadServices = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'services'));
      const servs = [];
      querySnapshot.forEach((doc) => {
        servs.push({ id: doc.id, ...doc.data() });
      });
      
      // ORDENAR ALFABÉTICAMENTE
      servs.sort((a, b) => a.name.localeCompare(b.name, 'es'));
      
      // Actualizar fechas de renovación si es necesario
      const updatedServs = await updateRenewalDatesIfNeeded(servs);
      
      setServices(updatedServs);
    } catch (error) {
      console.error('Error cargando servicios:', error);
    }
  };

  const loadServiceHistory = async (serviceId) => {
    try {
      const q = query(
        collection(db, 'serviceHistory'),
        where('serviceId', '==', serviceId)
      );
      const querySnapshot = await getDocs(q);
      const history = [];
      querySnapshot.forEach((doc) => {
        history.push({ id: doc.id, ...doc.data() });
      });
      history.sort((a, b) => {
        const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
        const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
        return dateB - dateA;
      });
      setServiceHistory(history);
    } catch (error) {
      console.error('Error cargando historial:', error);
    }
  };

  const saveToHistory = async (serviceId, serviceName, serviceData) => {
    try {
      await addDoc(collection(db, 'serviceHistory'), {
        serviceId: serviceId,
        serviceName: serviceName,
        date: Timestamp.now(),
        cost: serviceData.cost,
        users: serviceData.users || 0,
        frequency: serviceData.frequency,
        totalCost: calculateMonthlyCost(serviceData.cost, serviceData.frequency) * (serviceData.users || 1),
        responsible: serviceData.responsible,
        category: serviceData.category
      });
    } catch (error) {
      console.error('Error guardando historial:', error);
    }
  };

  // NUEVA FUNCIÓN: Generar datos mensuales para el gráfico
  const generateMonthlyChartData = (service, history) => {
    const monthlyData = [];
    
    // Si no hay historial, generar datos desde hace 6 meses hasta hoy
    if (history.length === 0) {
      const today = new Date();
      const startDate = new Date(service.renewalDate || today);
      
      // Empezar desde hace 6 meses o desde la fecha de renovación
      const sixMonthsAgo = new Date(today);
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      
      const actualStartDate = startDate > sixMonthsAgo ? startDate : sixMonthsAgo;
      
      let currentDate = new Date(actualStartDate);
      const monthlyCost = calculateMonthlyCost(service.cost, service.frequency) * (service.users || 1);
      
      while (currentDate <= today) {
        monthlyData.push({
          date: new Date(currentDate),
          totalCost: monthlyCost,
          users: service.users || 0,
          dateLabel: currentDate.toLocaleDateString('es-CL', { month: 'short', year: 'numeric' })
        });
        
        // Avanzar un mes
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
      
      return monthlyData;
    }
    
    // Si hay historial, generar datos mes a mes entre registros
    const sortedHistory = [...history].sort((a, b) => {
      const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
      const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
      return dateA - dateB;
    });
    
    const today = new Date();
    const firstRecord = sortedHistory[0];
    const firstDate = firstRecord.date?.toDate ? firstRecord.date.toDate() : new Date(firstRecord.date);
    
    let currentDate = new Date(firstDate);
    let currentRecordIndex = 0;
    
    while (currentDate <= today) {
      // Encontrar el registro activo en esta fecha
      while (
        currentRecordIndex < sortedHistory.length - 1 &&
        currentDate >= (sortedHistory[currentRecordIndex + 1].date?.toDate 
          ? sortedHistory[currentRecordIndex + 1].date.toDate() 
          : new Date(sortedHistory[currentRecordIndex + 1].date))
      ) {
        currentRecordIndex++;
      }
      
      const activeRecord = sortedHistory[currentRecordIndex];
      
      monthlyData.push({
        date: new Date(currentDate),
        totalCost: activeRecord.totalCost,
        users: activeRecord.users,
        dateLabel: currentDate.toLocaleDateString('es-CL', { month: 'short', year: 'numeric' })
      });
      
      // Avanzar un mes
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return monthlyData;
  };

  const handleSaveCategory = async () => {
    if (!categoryFormData.name.trim()) {
      alert('El nombre de la categoría es requerido');
      return;
    }

    try {
      if (editingCategory) {
        await updateDoc(doc(db, 'categories', editingCategory.id), {
          name: categoryFormData.name,
          color: categoryFormData.color
        });
      } else {
        await addDoc(collection(db, 'categories'), {
          name: categoryFormData.name,
          color: categoryFormData.color,
          order: categories.length + 1
        });
      }

      await loadCategories();
      resetCategoryForm();
    } catch (error) {
      console.error('Error guardando categoría:', error);
      alert('Error al guardar la categoría');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    const servicesWithCategory = services.filter(s => s.category === categoryName);

    if (servicesWithCategory.length > 0) {
      alert(`No se puede eliminar esta categoría porque hay ${servicesWithCategory.length} servicio(s) asignados a ella.`);
      return;
    }

    if (window.confirm(`¿Estás seguro de eliminar la categoría "${categoryName}"?`)) {
      try {
        await deleteDoc(doc(db, 'categories', categoryId));
        await loadCategories();
      } catch (error) {
        console.error('Error eliminando categoría:', error);
        alert('Error al eliminar la categoría');
      }
    }
  };

  const handleEditCategory = (category) => {
    setCategoryFormData({ name: category.name, color: category.color || 'blue' });
    setEditingCategory(category);
    setShowCategoryForm(true);
  };

  const resetCategoryForm = () => {
    setCategoryFormData({ name: '', color: 'blue' });
    setEditingCategory(null);
    setShowCategoryForm(false);
  };

  const handleViewServiceDetail = async (service) => {
    setSelectedService(service);
    await loadServiceHistory(service.id);
    setView('serviceDetail');
  };

  const handleSaveUser = async () => {
    if (!userFormData.email.trim() || !userFormData.name.trim()) {
      alert('Email y nombre son requeridos');
      return;
    }

    if (!userFormData.email.endsWith(ALLOWED_DOMAIN)) {
      alert(`El email debe ser del dominio ${ALLOWED_DOMAIN}`);
      return;
    }

    try {
      const userDocRef = doc(db, 'authorizedUsers', userFormData.email);
      
      if (editingUser) {
        // Actualizar usuario existente
        await updateDoc(userDocRef, {
          name: userFormData.name,
          role: userFormData.role,
          active: userFormData.active
        });

        // Manejar rol de admin
        const adminDocRef = doc(db, 'admins', userFormData.email);
        if (userFormData.role === 'Admin') {
          const adminDoc = await getDoc(adminDocRef);
          if (!adminDoc.exists()) {
            await setDoc(adminDocRef, {
              email: userFormData.email,
              isAdmin: true,
              grantedAt: Timestamp.now(),
              grantedBy: user.email
            });
          }
        } else {
          const adminDoc = await getDoc(adminDocRef);
          if (adminDoc.exists()) {
            await deleteDoc(adminDocRef);
          }
        }
      } else {
        // Crear nuevo usuario
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          alert('Este usuario ya existe');
          return;
        }

        await setDoc(userDocRef, {
          email: userFormData.email,
          name: userFormData.name,
          role: userFormData.role,
          active: userFormData.active,
          createdAt: Timestamp.now(),
          createdBy: user.email
        });

        // Si es admin, crear en colección admins
        if (userFormData.role === 'Admin') {
          const adminDocRef = doc(db, 'admins', userFormData.email);
          await setDoc(adminDocRef, {
            email: userFormData.email,
            isAdmin: true,
            grantedAt: Timestamp.now(),
            grantedBy: user.email
          });
        }
      }

      await loadAuthorizedUsers();
      resetUserForm();
      alert(editingUser ? 'Usuario actualizado exitosamente' : 'Usuario creado exitosamente');
    } catch (error) {
      console.error('Error guardando usuario:', error);
      alert('Error al guardar el usuario');
    }
  };

  const handleEditUser = (authUser) => {
    setUserFormData({
      email: authUser.email,
      name: authUser.name,
      role: authUser.role || 'Usuario',
      active: authUser.active !== false
    });
    setEditingUser(authUser);
    setShowUserForm(true);
  };

  const handleToggleUserStatus = async (email, currentStatus) => {
    if (email === user.email) {
      alert('No puedes desactivar tu propio usuario');
      return;
    }

    if (window.confirm(`¿Estás seguro de ${currentStatus ? 'desactivar' : 'activar'} este usuario?`)) {
      try {
        await updateDoc(doc(db, 'authorizedUsers', email), {
          active: !currentStatus
        });
        await loadAuthorizedUsers();
      } catch (error) {
        console.error('Error cambiando estado:', error);
        alert('Error al cambiar el estado del usuario');
      }
    }
  };

  const handleDeleteUser = async (email) => {
    if (email === user.email) {
      alert('No puedes eliminar tu propio usuario');
      return;
    }

    if (window.confirm(`¿Estás seguro de eliminar permanentemente al usuario ${email}?\n\nEsta acción no se puede deshacer.`)) {
      try {
        await deleteDoc(doc(db, 'authorizedUsers', email));
        
        // Eliminar también de admins si existe
        const adminDoc = await getDoc(doc(db, 'admins', email));
        if (adminDoc.exists()) {
          await deleteDoc(doc(db, 'admins', email));
        }

        await loadAuthorizedUsers();
        alert('Usuario eliminado exitosamente');
      } catch (error) {
        console.error('Error eliminando usuario:', error);
        alert('Error al eliminar el usuario');
      }
    }
  };

  const resetUserForm = () => {
    setUserFormData({
      email: '',
      name: '',
      role: 'Usuario',
      active: true
    });
    setEditingUser(null);
    setShowUserForm(false);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        if (!user.email.endsWith(ALLOWED_DOMAIN)) {
          await signOut(auth);
          setAuthError(`Solo usuarios con email ${ALLOWED_DOMAIN} pueden acceder`);
          setLoading(false);
          return;
        }

        const authorized = await checkUserAuthorization(user.email);
        if (!authorized) {
          await signOut(auth);
          setAuthError('Tu usuario no está autorizado. Contacta al administrador.');
          setLoading(false);
          return;
        }

        setIsAuthorized(true);
        const adminStatus = await checkIfAdmin(user.email);
        setIsAdmin(adminStatus);

        await loadCategories();
        await loadServices();

        if (adminStatus) {
          await loadAuthorizedUsers();
        }

        setUser(user);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        hd: 'soho.cl'
      });

      const result = await signInWithPopup(auth, provider);

      if (!result.user.email.endsWith(ALLOWED_DOMAIN)) {
        await signOut(auth);
        setAuthError(`Solo usuarios con email ${ALLOWED_DOMAIN} pueden acceder`);
        return;
      }

      const authorized = await checkUserAuthorization(result.user.email);
      if (!authorized) {
        await signOut(auth);
        setAuthError('Tu usuario no está autorizado. Contacta al administrador.');
        return;
      }

      setIsAuthorized(true);
      const adminStatus = await checkIfAdmin(result.user.email);
      setIsAdmin(adminStatus);

      await loadCategories();
      await loadServices();

      if (adminStatus) {
        await loadAuthorizedUsers();
      }

      setAuthError('');
    } catch (error) {
      setAuthError('Error al iniciar sesión. Intenta nuevamente.');
      console.error(error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setIsAuthorized(false);
      setIsAdmin(false);
      setAuthorizedUsers([]);
      setCategories([]);
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const [services, setServices] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [view, setView] = useState('dashboard');

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    cost: '',
    frequency: 'Mensual',
    users: '',
    renewalDate: '',
    responsible: '',
    status: 'Activo'
  });

  const frequencies = ['Mensual', 'Anual', 'Trimestral'];

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    purple: 'bg-purple-100 text-purple-700',
    orange: 'bg-orange-100 text-orange-700',
    red: 'bg-red-100 text-red-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    pink: 'bg-pink-100 text-pink-700',
    gray: 'bg-gray-100 text-gray-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    teal: 'bg-teal-100 text-teal-700'
  };

  const calculateMonthlyCost = (cost, frequency) => {
    if (frequency === 'Anual') return cost / 12;
    if (frequency === 'Trimestral') return cost / 3;
    return cost;
  };

  const calculateTotalMonthlyCost = () => {
    return services.reduce((sum, service) => sum + (calculateMonthlyCost(service.cost, service.frequency) * (service.users || 1)), 0);
  };

  const calculateTotalAnnualCost = () => {
    return calculateTotalMonthlyCost() * 12;
  };

  const getCostByCategory = () => {
    const categoryTotals = {};
    services.forEach(service => {
      const monthlyCost = calculateMonthlyCost(service.cost, service.frequency) * (service.users || 1);
      categoryTotals[service.category] = (categoryTotals[service.category] || 0) + monthlyCost;
    });
    return categoryTotals;
  };

  const getUpcomingRenewals = () => {
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

    return services.filter(service => {
      const renewalDate = new Date(service.renewalDate);
      return renewalDate >= today && renewalDate <= thirtyDaysFromNow;
    }).sort((a, b) => new Date(a.renewalDate) - new Date(b.renewalDate));
  };

  const handleSubmit = async () => {
    try {
      if (editingService) {
        await updateDoc(doc(db, 'services', editingService.id), formData);
        await saveToHistory(editingService.id, formData.name, formData);
      } else {
        const docRef = await addDoc(collection(db, 'services'), formData);
        await saveToHistory(docRef.id, formData.name, formData);
      }
      await loadServices();
      resetForm();
    } catch (error) {
      console.error('Error guardando servicio:', error);
      alert('Error al guardar el servicio');
    }
  };

  const handleEdit = (service) => {
    setFormData(service);
    setEditingService(service);
    setShowForm(true);
    setView('table');
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este servicio?')) {
      try {
        await deleteDoc(doc(db, 'services', id));
        await loadServices();
      } catch (error) {
        console.error('Error eliminando servicio:', error);
        alert('Error al eliminar el servicio');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      cost: '',
      frequency: 'Mensual',
      users: '',
      renewalDate: '',
      responsible: '',
      status: 'Activo'
    });
    setShowForm(false);
    setEditingService(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CL', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('es-CL', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getDaysUntilRenewal = (dateString) => {
    const today = new Date();
    const renewal = new Date(dateString);
    const diffTime = renewal - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Verificando acceso...</p>
        </div>
      </div>
    );
  }

  if (!user || !isAuthorized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-2xl p-12 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-100 rounded-full mb-4">
              <Shield className="w-10 h-10 text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Control de Licencias y Suscripciones</h1>
            <p className="text-gray-600">Gestión de Herramientas y Licencias - Soho</p>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 font-semibold">🔒 Acceso Restringido</p>
              <p className="text-xs text-blue-600 mt-1">Solo usuarios autorizados de {ALLOWED_DOMAIN}</p>
            </div>
          </div>

          <div className="space-y-4">
            <button
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 text-gray-700 px-6 py-4 rounded-lg font-semibold hover:bg-gray-50 hover:border-gray-400 transition shadow-sm"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Iniciar sesión con Google
            </button>

            {authError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Acceso Denegado</p>
                  <p className="text-xs mt-1">{authError}</p>
                </div>
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-500">
                Si necesitas acceso, contacta al administrador
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 mb-2">Control de Licencias y Suscripciones</h1>
              <p className="text-gray-600">Gestión de Herramientas y Licencias - Soho</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end">
                  <p className="font-semibold text-gray-800">{user.displayName}</p>
                  {isAdmin && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-semibold flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      Admin
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>
              {user.photoURL && (
                <img src={user.photoURL} alt="Avatar" className="w-12 h-12 rounded-full border-2 border-indigo-200" />
              )}
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {view !== 'serviceDetail' && (
          <div className="bg-white rounded-xl shadow-md p-4 mb-6 flex gap-4 flex-wrap">
            <button
              onClick={() => setView('dashboard')}
              className={`px-6 py-2 rounded-lg font-semibold transition flex items-center gap-2 ${view === 'dashboard' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              <LayoutDashboard className="w-4 h-4" />
              Dashboard
            </button>
            <button
              onClick={() => setView('table')}
              className={`px-6 py-2 rounded-lg font-semibold transition flex items-center gap-2 ${view === 'table' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              <Package className="w-4 h-4" />
              Servicios
            </button>
            {isAdmin && (
              <>
                <button
                  onClick={() => setView('categories')}
                  className={`px-6 py-2 rounded-lg font-semibold transition flex items-center gap-2 ${view === 'categories' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  <Tag className="w-4 h-4" />
                  Categorías
                </button>
                <button
                  onClick={() => setView('users')}
                  className={`px-6 py-2 rounded-lg font-semibold transition flex items-center gap-2 ${view === 'users' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  <Shield className="w-4 h-4" />
                  Usuarios
                </button>
              </>
            )}
          </div>
        )}

        {view === 'serviceDetail' && selectedService && (
          <div className="space-y-6">
            <button
              onClick={() => {
                setView('table');
                setSelectedService(null);
                setServiceHistory([]);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver a Servicios
            </button>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-gray-800">{selectedService.name}</h2>
                  <p className="text-gray-600 mt-1">Historial de Consumo y Cambios</p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">Costo Actual</div>
                  <div className="text-3xl font-bold text-indigo-600">
                    {formatCurrency(calculateMonthlyCost(selectedService.cost, selectedService.frequency) * (selectedService.users || 1))}
                  </div>
                  <div className="text-sm text-gray-500">por mes</div>
                </div>
              </div>

              {/* GRÁFICO MODIFICADO: Ahora muestra datos mensuales */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Tendencia de Costo Mensual</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={generateMonthlyChartData(selectedService, serviceHistory)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="dateLabel"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      labelFormatter={(label) => label}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="totalCost" 
                      stroke="#6366f1" 
                      strokeWidth={2}
                      name="Costo Total Mensual"
                      dot={{ fill: '#6366f1', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {serviceHistory.length > 0 ? (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Historial de Cambios</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Fecha</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Costo/Usuario</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Usuarios</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Frecuencia</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Total Mensual</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Responsable</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {serviceHistory.map((record) => (
                          <tr key={record.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-700">{formatDateTime(record.date)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(record.cost)}</td>
                            <td className="px-4 py-3 text-center text-gray-700">{record.users || 0}</td>
                            <td className="px-4 py-3 text-gray-700">{record.frequency}</td>
                            <td className="px-4 py-3 text-right font-semibold text-gray-800">{formatCurrency(record.totalCost)}</td>
                            <td className="px-4 py-3 text-gray-700">{record.responsible}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">El gráfico muestra la proyección mensual del servicio</p>
                  <p className="text-gray-400 text-sm mt-1">El historial de cambios comenzará a acumularse cuando edites este servicio</p>
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'categories' && isAdmin && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Tag className="w-6 h-6 text-indigo-600" />
                Gestión de Categorías
              </h2>
              <button
                onClick={() => setShowCategoryForm(!showCategoryForm)}
                className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
              >
                <Plus className="w-5 h-5" />
                {showCategoryForm ? 'Cancelar' : 'Nueva Categoría'}
              </button>
            </div>

            {showCategoryForm && (
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Nombre de la Categoría</label>
                    <input
                      type="text"
                      value={categoryFormData.name}
                      onChange={(e) => setCategoryFormData({...categoryFormData, name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Ej: Marketing"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Color</label>
                    <select
                      value={categoryFormData.color}
                      onChange={(e) => setCategoryFormData({...categoryFormData, color: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="blue">Azul</option>
                      <option value="green">Verde</option>
                      <option value="purple">Morado</option>
                      <option value="orange">Naranja</option>
                      <option value="red">Rojo</option>
                      <option value="indigo">Índigo</option>
                      <option value="pink">Rosa</option>
                      <option value="yellow">Amarillo</option>
                      <option value="teal">Verde azulado</option>
                      <option value="gray">Gris</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 justify-end mt-4">
                  <button
                    onClick={resetCategoryForm}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveCategory}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {editingCategory ? 'Actualizar' : 'Guardar'}
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => {
                const servicesCount = services.filter(s => s.category === category.name).length;
                return (
                  <div key={category.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200 hover:shadow-md transition">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${colorClasses[category.color] || colorClasses.gray}`}>
                        {category.name}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditCategory(category)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id, category.name)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      {servicesCount} {servicesCount === 1 ? 'servicio' : 'servicios'}
                    </p>
                  </div>
                );
              })}
            </div>

            {categories.length === 0 && (
              <div className="text-center py-12">
                <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No hay categorías creadas</p>
                <p className="text-gray-400 text-sm">Crea tu primera categoría para organizar los servicios</p>
              </div>
            )}
          </div>
        )}

        {view === 'users' && isAdmin && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Shield className="w-6 h-6 text-purple-600" />
                Gestión de Usuarios Autorizados
              </h2>
              <button
                onClick={() => setShowUserForm(!showUserForm)}
                className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition"
              >
                <Plus className="w-5 h-5" />
                {showUserForm ? 'Cancelar' : 'Nuevo Usuario'}
              </button>
            </div>

            {showUserForm && (
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email {!editingUser && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="email"
                      value={userFormData.email}
                      onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                      disabled={editingUser !== null}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                      placeholder="usuario@soho.cl"
                    />
                    {!editingUser && (
                      <p className="text-xs text-gray-500 mt-1">Debe ser del dominio @soho.cl</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nombre Completo <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={userFormData.name}
                      onChange={(e) => setUserFormData({...userFormData, name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Nombre Apellido"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Rol</label>
                    <select
                      value={userFormData.role}
                      onChange={(e) => setUserFormData({...userFormData, role: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="Usuario">Usuario</option>
                      <option value="Admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Estado</label>
                    <select
                      value={userFormData.active}
                      onChange={(e) => setUserFormData({...userFormData, active: e.target.value === 'true'})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="true">Activo</option>
                      <option value="false">Inactivo</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 justify-end mt-4">
                  <button
                    onClick={resetUserForm}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveUser}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {editingUser ? 'Actualizar' : 'Crear Usuario'}
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Nombre</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Rol</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Estado</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {authorizedUsers.map((authUser) => (
                    <tr key={authUser.email} className={`hover:bg-gray-50 ${!authUser.active ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3 font-mono text-sm">
                        {authUser.email}
                        {authUser.email === user.email && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Tú</span>
                        )}
                      </td>
                      <td className="px-4 py-3">{authUser.name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          authUser.role === 'Admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {authUser.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {authUser.active !== false ? (
                          <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                            <UserCheck className="w-4 h-4" />
                            Activo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600 text-sm">
                            <UserX className="w-4 h-4" />
                            Inactivo
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={() => handleEditUser(authUser)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Editar usuario"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleUserStatus(authUser.email, authUser.active !== false)}
                            disabled={authUser.email === user.email}
                            className={`p-2 rounded ${
                              authUser.email === user.email 
                                ? 'text-gray-400 cursor-not-allowed' 
                                : authUser.active !== false
                                  ? 'text-orange-600 hover:bg-orange-50'
                                  : 'text-green-600 hover:bg-green-50'
                            }`}
                            title={authUser.active !== false ? 'Desactivar' : 'Activar'}
                          >
                            {authUser.active !== false ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleDeleteUser(authUser.email)}
                            disabled={authUser.email === user.email}
                            className={`p-2 rounded ${
                              authUser.email === user.email 
                                ? 'text-gray-400 cursor-not-allowed' 
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                            title="Eliminar usuario"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {authorizedUsers.length === 0 && (
              <div className="text-center py-12">
                <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No hay usuarios autorizados</p>
                <p className="text-gray-400 text-sm mt-2">Crea el primer usuario para comenzar</p>
              </div>
            )}
          </div>
        )}

        {view === 'dashboard' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="w-8 h-8" />
                  <span className="text-sm opacity-90">Mensual</span>
                </div>
                <div className="text-3xl font-bold">{formatCurrency(calculateTotalMonthlyCost())}</div>
                <div className="text-sm opacity-90 mt-1">Costo total mensual</div>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-8 h-8" />
                  <span className="text-sm opacity-90">Anual</span>
                </div>
                <div className="text-3xl font-bold">{formatCurrency(calculateTotalAnnualCost())}</div>
                <div className="text-sm opacity-90 mt-1">Proyección anual</div>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between mb-2">
                  <Calendar className="w-8 h-8" />
                  <span className="text-sm opacity-90">Servicios</span>
                </div>
                <div className="text-3xl font-bold">{services.length}</div>
                <div className="text-sm opacity-90 mt-1">Herramientas activas</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-6 h-6 text-orange-500" />
                  Renovaciones Próximas (30 días)
                </h2>
                {getUpcomingRenewals().length > 0 ? (
                  <div className="space-y-3">
                    {getUpcomingRenewals().map(service => (
                      <div key={service.id} className="flex items-center justify-between p-4 bg-orange-50 border-l-4 border-orange-500 rounded-lg">
                        <div>
                          <div className="font-semibold text-gray-800">{service.name}</div>
                          <div className="text-sm text-gray-600">{formatDate(service.renewalDate)}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-orange-600">{getDaysUntilRenewal(service.renewalDate)} días</div>
                          <div className="text-sm text-gray-600">{formatCurrency(service.cost * (service.users || 1))}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No hay renovaciones próximas</p>
                )}
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Costos por Categoría</h2>
                <div className="space-y-3">
                  {Object.entries(getCostByCategory()).map(([category, cost]) => (
                    <div key={category} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <span className="font-semibold text-gray-700">{category}</span>
                      <div className="text-right">
                        <div className="font-bold text-indigo-600">{formatCurrency(cost)}/mes</div>
                        <div className="text-sm text-gray-500">{formatCurrency(cost * 12)}/año</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {view === 'table' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
              <h2 className="text-2xl font-bold text-gray-800">Servicios y Licencias</h2>
              <button
                onClick={() => setShowForm(!showForm)}
                className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
              >
                <Plus className="w-5 h-5" />
                {showForm ? 'Cancelar' : 'Agregar'}
              </button>
            </div>

            {showForm && (
              <div className="bg-gray-50 p-6 rounded-lg mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Servicio</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Categoría</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Seleccionar...</option>
                    {categories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Costo</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({...formData, cost: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Frecuencia</label>
                  <select
                    value={formData.frequency}
                    onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    {frequencies.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Usuarios</label>
                  <input
                    type="number"
                    value={formData.users}
                    onChange={(e) => setFormData({...formData, users: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Renovación</label>
                  <input
                    type="date"
                    value={formData.renewalDate}
                    onChange={(e) => setFormData({...formData, renewalDate: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Responsable</label>
                  <input
                    type="text"
                    value={formData.responsible}
                    onChange={(e) => setFormData({...formData, responsible: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Estado</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Activo">Activo</option>
                    <option value="Por renovar">Por renovar</option>
                    <option value="Cancelado">Cancelado</option>
                  </select>
                </div>
                <div className="md:col-span-2 flex gap-3 justify-end">
                  <button onClick={resetForm} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg">
                    Cancelar
                  </button>
                  <button onClick={handleSubmit} className="px-6 py-2 bg-indigo-600 text-white rounded-lg">
                    {editingService ? 'Actualizar' : 'Guardar'}
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Servicio</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Categoría</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Costo/Usuario</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Usuarios</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Total Mensual</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Renovación</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {services.map(service => {
                    const monthlyCost = calculateMonthlyCost(service.cost, service.frequency);
                    const totalMonthlyCost = monthlyCost * (service.users || 1);
                    const category = categories.find(c => c.name === service.category);
                    return (
                      <tr key={service.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-semibold">{service.name}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            category ? colorClasses[category.color] || colorClasses.gray : colorClasses.gray
                          }`}>
                            {service.category}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">{formatCurrency(monthlyCost)}</td>
                        <td className="px-4 py-3 text-center">{service.users || '-'}</td>
                        <td className="px-4 py-3 text-right font-semibold">{formatCurrency(totalMonthlyCost)}</td>
                        <td className="px-4 py-3 text-sm">{formatDate(service.renewalDate)}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2 justify-center">
                            <button 
                              onClick={() => handleViewServiceDetail(service)} 
                              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded"
                              title="Ver detalle"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleEdit(service)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(service.id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-100 font-bold">
                  <tr>
                    <td colSpan="4" className="px-4 py-3 text-right">TOTAL:</td>
                    <td className="px-4 py-3 text-right text-indigo-600 text-lg">{formatCurrency(calculateTotalMonthlyCost())}</td>
                    <td colSpan="2" className="px-4 py-3 text-sm">Anual: {formatCurrency(calculateTotalAnnualCost())}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CostTracker;